import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { UserDTO } from 'services/api/models/UserDTO';

const LONG_PRESS_DURATION_MS = 500;

/** Keeps the menu clear of the viewport edges. */
const VIEWPORT_MARGIN = 8;

/**
 * How long after opening the menu a scroll is assumed to be the list settling
 * rather than the user scrolling away. Long enough to cover the auto-scroll,
 * short enough that a real swipe still dismisses the menu.
 */
const SCROLL_DISMISS_GRACE_MS = 400;

export type MessageContextMenuState = {
  visible: boolean;
  /** Set for left-anchored menus (other people's messages). */
  x: number;
  /** Set instead of x for right-anchored menus (own messages) so the browser
   *  resolves width before placement, avoiding off-screen overflow. */
  right?: number;
  y: number;
  message: ConversationMessage | null;
};

type UseMessageContextMenuOptions = {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  messagesRef: React.MutableRefObject<ConversationMessage[]>;
  currentUser: UserDTO | null;
};

/**
 * Detects right-click / long-press gestures on message bubbles and tracks
 * which message is selected in the floating context menu.
 *
 * Intentionally limited to gesture detection and menu-visibility state.
 * The actual edit / delete / copy logic lives in the consuming component.
 */
export function useMessageContextMenu({ containerRef, messagesRef, currentUser }: UseMessageContextMenuOptions) {
  const [contextMenu, setContextMenu] = useState<MessageContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    message: null,
  });

  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const openedAtRef = useRef(0);

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    // Pull the menu back inside the viewport once its real height is known.
    //
    // The initial placement below uses an *estimated* height, because the menu
    // has not rendered yet at that point. When the estimate is short — a font
    // change, a longer label wrapping to two lines — the last item hangs below
    // the fold. That is a real problem for a user on a small screen, and it
    // also makes the menu unclickable in tests: the click scrolls the item into
    // view, and the scroll listener above treats any scroll as "interacted
    // outside" and closes the menu, so the click never lands.
    //
    // Measuring after layout and clamping fixes both. The guard keeps this to a
    // single extra render.
    if (!contextMenu.visible || !contextMenuRef.current) return;
    const { height } = contextMenuRef.current.getBoundingClientRect();
    const maxY = window.innerHeight - height - VIEWPORT_MARGIN;
    const clampedY = Math.max(VIEWPORT_MARGIN, Math.min(contextMenu.y, maxY));
    if (Math.abs(clampedY - contextMenu.y) > 0.5) {
      setContextMenu(prev => ({ ...prev, y: clampedY }));
    }
  }, [contextMenu.visible, contextMenu.y, contextMenu.message]);

  useEffect(() => {
    // Close context menu when the user interacts outside it.
    // We use persistent listeners (no { once }) so that a tap *inside* the menu
    // (which we skip) does not consume the listener, leaving future outside taps
    // unable to close the menu. The effect cleanup removes them when the menu
    // closes or the component unmounts.
    if (!contextMenu.visible) return;
    const handleOutsideInteraction = (e: Event) => {
      // Ignore events that originate inside the context menu — let the item's
      // own onClick / React synthetic handler fire normally.
      if (contextMenuRef.current?.contains(e.target as Node)) return;
      setContextMenu(prev => ({ ...prev, visible: false }));
    };
    const handleScroll = (e: Event) => {
      // Only a scroll the *user* performed should dismiss the menu.
      //
      // The message list settles itself right after the menu opens — it keeps
      // itself pinned to the newest message, and bringing the pressed bubble
      // into view can trigger that. Treating those programmatic scrolls as
      // "the user scrolled away" made the menu flicker out from under the
      // press, which is both wrong for the user and why the delete flow could
      // not be driven in tests.
      if (performance.now() - openedAtRef.current < SCROLL_DISMISS_GRACE_MS) return;
      handleOutsideInteraction(e);
    };
    document.addEventListener('click', handleOutsideInteraction);
    document.addEventListener('touchstart', handleOutsideInteraction);
    document.addEventListener('scroll', handleScroll, { capture: true });
    return () => {
      document.removeEventListener('click', handleOutsideInteraction);
      document.removeEventListener('touchstart', handleOutsideInteraction);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [contextMenu.visible]);

  function getMessageAndContainerFromTarget(
    target: EventTarget | null,
  ): { message: ConversationMessage; container: HTMLElement } | null {
    if (!target || !containerRef.current) return null;
    let el = target as HTMLElement;
    while (el && !el.classList.contains('rce-container-mbox')) {
      if (el === containerRef.current) return null;
      el = el.parentElement as HTMLElement;
    }
    if (!el) return null;
    const boxes = Array.from(containerRef.current.querySelectorAll('.rce-container-mbox'));
    const idx = boxes.indexOf(el);
    if (idx < 0 || idx >= messagesRef.current.length) return null;
    return { message: messagesRef.current[idx], container: el };
  }

  function showContextMenuForMessage(message: ConversationMessage, container: HTMLElement) {
    if (!currentUser) return;
    openedAtRef.current = performance.now();
    // Show the menu for all non-deleted messages. Edit/delete are only rendered
    // for own messages (checked at render time in MessagesList).
    if (message.is_deleted) return;

    const rect = container.getBoundingClientRect();
    const isOwnMessage = message.sender.id === currentUser.id;
    // Rough height for the first paint only — the layout effect above measures
    // the real menu and clamps it into the viewport straight after.
    const estimatedMenuHeight = isOwnMessage ? 132 : 44;
    const gap = 4;

    // Place below the message if there's room, otherwise above
    const spaceBelow = window.innerHeight - rect.bottom;
    const y = spaceBelow >= estimatedMenuHeight + gap ? rect.bottom + gap : rect.top - estimatedMenuHeight - gap;

    const edgeMargin = 16;

    if (isOwnMessage) {
      // Use `right` so the browser resolves the actual rendered width before
      // placing the menu — avoids off-screen overflow from guessing the width in JS.
      setContextMenu({ visible: true, x: 0, right: edgeMargin, y: Math.max(VIEWPORT_MARGIN, y), message });
    } else {
      const x = Math.max(VIEWPORT_MARGIN, rect.left + edgeMargin);
      setContextMenu({ visible: true, x, y: Math.max(VIEWPORT_MARGIN, y), message });
    }
  }

  // Right-click / context menu
  function handleContextMenu(e: React.MouseEvent) {
    const result = getMessageAndContainerFromTarget(e.target);
    if (!result) return;
    e.preventDefault();
    showContextMenuForMessage(result.message, result.container);
  }

  // Touch long-press
  function handleTouchStart(e: React.TouchEvent) {
    const touch = e.touches[0];
    // Guard against synthetic touchstart events that carry no touches
    // (e.g. dispatched by Playwright's click(button="right") on touch profiles).
    if (!touch) return;
    longPressStartPosRef.current = { x: touch.clientX, y: touch.clientY };
    const target = e.target;

    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      const result = getMessageAndContainerFromTarget(target);
      if (!result) return;
      showContextMenuForMessage(result.message, result.container);
    }, LONG_PRESS_DURATION_MS);
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!longPressStartPosRef.current || !longPressTimerRef.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    const dx = touch.clientX - longPressStartPosRef.current.x;
    const dy = touch.clientY - longPressStartPosRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > 8) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }

  function handleTouchEnd() {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }

  function closeContextMenu() {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }

  return {
    contextMenu,
    contextMenuRef,
    closeContextMenu,
    handleContextMenu,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}
